import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { LaborBillingFormatEnum } from '@graphql/platform';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { locationsURL } from '@root/components/locations/routes';
import { COMMITMENT_TO_FREE_SUBSEQUENT_JOBS } from '@root/constants/pricing';

import { formatCurrency } from '@utils/formatters';
import { useLastDefined } from '@utils/hooks/use_last_defined';
import { pluralize } from '@utils/pluralize';

import closeIcon from '@images/checkout/challenger/close.svg';

import { useModalFocus } from './hooks/use_modal_focus';
import { CHALLENGER as T } from './tokens';

/** Matches `useBreakpoints().isDesktop` (MD_INT = 840). */
const DESKTOP = '@media (min-width: 840px)';
const MOBILE = '@media (max-width: 839px)';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  justify-content: center;
  padding: ${T.space[16]};
  box-sizing: border-box;
  background: rgba(4, 7, 17, 0.4);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  ${MOBILE} {
    align-items: flex-start;
    padding: ${T.space[32]} ${T.space[16]};
  }

  ${DESKTOP} {
    align-items: safe center;
  }
`;

/**
 * Funnel-Feedback 280:4360 — 560px wide. Viewport is 610px; body scrolls.
 */
const Dialog = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  max-width: 560px;
  height: 610px;
  max-height: min(610px, calc(100vh - 32px));
  max-height: min(610px, calc(100dvh - 32px));
  padding: ${T.space[24]};
  box-sizing: border-box;
  border-radius: 16px;
  background: ${T.color.bgPage};
  font-family: ${T.font.sans};
  color: ${T.color.textPrimary};
  overflow: hidden;

  ${MOBILE} {
    max-height: min(610px, calc(100vh - 64px));
    max-height: min(610px, calc(100dvh - 64px));
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[16]};
  width: 100%;
  height: 100%;
  min-height: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  gap: ${T.space[16]};
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 20px;
  font-weight: ${FontWeight.Medium};
  line-height: 23px;
  color: ${T.color.textPrimary};
`;

/** Close Icon 24×24; vector inset 20.83% → 14×14 leaf. */
const CloseButton = styled.button`
  appearance: none;
  position: relative;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;
  overflow: hidden;

  img {
    position: absolute;
    inset: 20.83%;
    display: block;
    width: 14px;
    height: 14px;
  }
`;

const Body = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: ${T.color.textSubtle} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${T.color.textSubtle};
    border-radius: ${T.radius.xs};
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[8]};
  width: 100%;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${T.space[16]};
  width: 100%;
  padding-bottom: ${T.space[8]};
  box-sizing: border-box;
`;

const SectionTitle = styled.h3`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 19px;
  color: ${T.color.textPrimary};
`;

const SectionBody = styled.p`
  margin: 0;
  width: 100%;
  font-size: 14px;
  font-weight: ${FontWeight.Regular};
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const LocationsLink = styled(Link)`
  color: ${T.color.linkBrand};
  font-family: ${T.font.sans};
  font-size: 14px;
  font-weight: ${FontWeight.Semibold};
  line-height: 20px;
  text-decoration: none;

  &:hover {
    opacity: 0.85;
  }
`;

const RateTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  background: ${T.color.summarySurface};
`;

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[24]};
  width: 100%;
  padding: ${T.space[16]} ${T.space[16]} ${T.space[12]};
  box-sizing: border-box;
  border-radius: ${T.radius.sm} ${T.radius.sm} 0 0;
`;

const TableRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 ${T.space[8]};
  box-sizing: border-box;

  &:last-of-type {
    border-radius: 0 0 ${T.radius.sm} ${T.radius.sm};
  }
`;

const TableRowInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${T.space[24]};
  width: 100%;
  padding: ${T.space[16]} ${T.space[8]};
  box-sizing: border-box;
  border-top: 1px solid ${T.color.borderDefault};
`;

const HeaderItem = styled.div`
  flex: 1 0 0;
  max-width: 164px;
  min-width: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};
`;

const HeaderCost = styled.div`
  flex: 1 0 0;
  min-width: 0;
  font-size: 16px;
  font-weight: ${FontWeight.Medium};
  line-height: 24px;
  color: ${T.color.textPrimary};
`;

const ItemCell = styled.div`
  flex: 1 0 0;
  max-width: 164px;
  min-width: 0;
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textPrimary};
`;

const CostCell = styled.div`
  flex: 1 0 0;
  min-width: 0;
  font-size: 14px;
  font-weight: ${FontWeight.Medium};
  line-height: 21px;
  color: ${T.color.textPrimary};
`;

type RateRow = {
  label: string;
  cost: string;
};

const PricingTable: React.FC<{
  leftHeader: string;
  rows: RateRow[];
}> = ({ leftHeader, rows }) => (
  <RateTable>
    <TableHeader>
      <HeaderItem>{leftHeader}</HeaderItem>
      <HeaderCost>Cost</HeaderCost>
    </TableHeader>
    {rows.map((row) => (
      <TableRow key={row.label}>
        <TableRowInner>
          <ItemCell>{row.label}</ItemCell>
          <CostCell>{row.cost}</CostCell>
        </TableRowInner>
      </TableRow>
    ))}
  </RateTable>
);

const formatHourlyRate = (
  amount: number | undefined,
  laborBillingFormat?: LaborBillingFormatEnum,
) => {
  if (amount == null || amount <= 0) return null;
  const perMover =
    laborBillingFormat === LaborBillingFormatEnum.PerMoverHour
      ? ' per mover'
      : '';
  return `${formatCurrency(amount, {
    maximumFractionDigits: 0,
  })}${perMover} per hour`;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const FinalDeliveryCostModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useModalFocus(isOpen, dialogRef, closeButtonRef);

  const {
    pricingSummary: pricingSummaryRaw,
    defaultPricingSummary,
    flowState: {
      values: { commitment },
    },
  } = useStorageCheckoutContext();
  /** Keep last quote so dynamic $ / hours don’t blank on refetch. */
  const pricingSummary =
    useLastDefined(pricingSummaryRaw) ?? defaultPricingSummary;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const onOverlayClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    if (event.currentTarget === event.target) onClose();
  };

  const freeLaborHours =
    pricingSummary?.freeDuration != null
      ? Math.round(pricingSummary.freeDuration)
      : 2;
  const freeJobs =
    commitment != null ? COMMITMENT_TO_FREE_SUBSEQUENT_JOBS[commitment] : 0;
  const includedRatePhrase = formatHourlyRate(
    pricingSummary?.fullServiceLaborRateAmount,
    pricingSummary?.laborBillingFormat,
  );
  const otherRatePhrase =
    formatHourlyRate(
      pricingSummary?.maxLaborRateAmount,
      pricingSummary?.laborBillingFormat,
    ) ?? includedRatePhrase;
  const complimentaryHoursLabel = pluralize(
    freeLaborHours,
    'complimentary hour',
    'complimentary hours',
  );
  const includedCost = includedRatePhrase
    ? `${complimentaryHoursLabel} and then ${includedRatePhrase}`
    : null;
  const pickupFee = formatCurrency(50, { maximumFractionDigits: 0 });
  const pricingDetailsAvailable =
    includedCost != null && otherRatePhrase != null;

  const pickupRows: RateRow[] = [
    {
      label: 'Initial pickup',
      cost: includedCost ?? '',
    },
    ...(freeJobs > 0
      ? [
          {
            label: 'Included pickup(s)',
            cost: `For each pickup, ${includedCost}`,
          },
        ]
      : []),
    {
      label: 'All other pickups',
      cost: `${otherRatePhrase} with a 1 hour minimum`,
    },
  ];

  const deliveryRows: RateRow[] = [
    {
      label: 'Final delivery',
      cost: includedCost ?? '',
    },
    ...(freeJobs > 0
      ? [
          {
            label: 'Included delivery/deliveries',
            cost: `For each delivery, ${includedCost}`,
          },
        ]
      : []),
    {
      label: 'All other deliveries',
      cost: `${otherRatePhrase} with a 1 hour minimum`,
    },
  ];

  const otherRows: RateRow[] = [
    { label: 'Pick Them Up', cost: `${pickupFee}+` },
    {
      label: 'Shipped to You',
      cost: 'If an item is eligible for shipping, the cost is calculated per item',
    },
  ];

  return createPortal(
    <Overlay onClick={onOverlayClick} role="presentation">
      <Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenger-pickup-delivery-details-title"
        onClick={(event) => event.stopPropagation()}
      >
        <Container>
          <HeaderRow>
            <Title id="challenger-pickup-delivery-details-title">
              Pickup and delivery details
            </Title>
            <CloseButton
              ref={closeButtonRef}
              type="button"
              aria-label="Close"
              onClick={onClose}
            >
              <img src={closeIcon} alt="" width={14} height={14} />
            </CloseButton>
          </HeaderRow>

          <Body>
            {!pricingDetailsAvailable ? (
              <Section>
                <SectionBody>
                  Pricing details are loading. Please wait a moment and try
                  again.
                </SectionBody>
              </Section>
            ) : (
              <>
                <Section>
                  <SectionTitle>
                    Complimentary hours &amp; hourly rates
                  </SectionTitle>
                  <SectionBody>
                    Pickups and deliveries are based on an hourly rate.
                    Customers may receive complimentary hours for certain
                    pickups and deliveries. Complimentary hours are applied to
                    the time our team spends on-site and begin once our team
                    arrives at your address. If your pickup or delivery exceeds
                    your complimentary hours or is not eligible for
                    complimentary hours, you will be charged based on your
                    hourly rate.
                  </SectionBody>
                  <SectionBody>
                    For example: If you have 2 complimentary hours but your
                    pickup takes 3 hours, you will pay for 1 hour at your hourly
                    rate.
                  </SectionBody>
                </Section>

                <Block>
                  <Section>
                    <SectionTitle>
                      Pickup pricing and service areas
                    </SectionTitle>
                    <SectionBody>
                      We only pick up from one address per pickup. If we are
                      storing items from multiple addresses, you will need to
                      schedule multiple pickups or consolidate everything to a
                      single address. You will be charged upon completion of a
                      pickup.
                    </SectionBody>
                  </Section>
                  <PricingTable leftHeader="Pickups" rows={pickupRows} />
                </Block>

                <Block>
                  <Section>
                    <SectionTitle>Delivery locations and pricing</SectionTitle>
                    <SectionBody>
                      We only deliver items back to addresses in the service
                      area where your items were initially picked up. You can
                      find a list of our delivery areas{' '}
                      <LocationsLink
                        to={locationsURL()}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </LocationsLink>
                      . If you are located outside of our delivery area, you can
                      access your items by picking them up directly from our
                      storage facility. You will be charged upon completion of a
                      delivery.
                    </SectionBody>
                  </Section>
                  <PricingTable leftHeader="Deliveries" rows={deliveryRows} />
                </Block>

                <Block>
                  <Section>
                    <SectionTitle>
                      Other ways to access your items:
                    </SectionTitle>
                    <SectionBody>
                      If our team cannot deliver your items, you can come to our
                      storage facility and pick them up. Some items are eligible
                      to be shipped to you, including boxes with non-fragile
                      items. Furniture and large pieces are not eligible for
                      shipping.
                    </SectionBody>
                  </Section>
                  <PricingTable
                    leftHeader="Other Delivery Methods"
                    rows={otherRows}
                  />
                </Block>
              </>
            )}
          </Body>
        </Container>
      </Dialog>
    </Overlay>,
    document.body,
  );
};
